import React, { useState, useEffect, useRef } from "react";
import LapList from "./components/LapList";
import HowToButton from "./components/HowToButton";
import RaceLineReplay from "./components/RaceLineReplay";
import InputsGraph from "./components/InputsGraph";
import { Box, Typography, Button, Container, CssBaseline, ThemeProvider, createTheme, GlobalStyles, Switch, Snackbar, FormControl, InputLabel, Select, MenuItem, ToggleButton, ToggleButtonGroup } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import QueuePage from "./components/QueuePage";
import MuiAlert from '@mui/material/Alert';
import { authFetch } from './utils/authFetch';
import DeleteLapsDropdown from "./components/DeleteLapsDropdown";
import LapComparison from "./components/LapComparison";
import DownloadsPage from "./components/DownloadsPage";
import SessionInfoBox from "./components/SessionInfoBox";
import LapControls from "./components/LapControls";
import { handleDownloadLaps, handleUploadLaps } from './utils/lapHandlers';
import Footer from "./components/Footer";
import PrivacyPolicy from "./components/PrivacyPolicy";
import SimpleRouter from "./SimpleRouter";
import TermsOfService from "./components/TermsOfService";
import CookieConsent from "./components/CookieConsent";
import AdBanner from "./components/AdBanner";
import AdBannerSide from "./components/AdBannerSide";

// Create F1-style theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#0072CE', // GT blue
    },
    secondary: {
      main: '#FF1E1E', // F1 red
    },
    background: {
      default: '#10182a', // GT7 dark blue
      paper: '#1a2238',  // Slightly lighter dark card
    },
    text: {
      primary: '#fff',
      secondary: '#b0c4de',
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
    h1: {
      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '2.5rem',
      fontWeight: 600,
      color: '#FFFFFF',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
    h2: {
      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '2rem',
      fontWeight: 600,
      color: '#FFFFFF',
      letterSpacing: '0.05em',
    },
    h3: {
      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 500,
      color: '#FFFFFF',
      letterSpacing: '0.05em',
    },
    h4: {
      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 500,
      color: '#FFFFFF',
      letterSpacing: '0.05em',
    },
    h5: {
      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '1.1rem',
      fontWeight: 500,
      color: '#FFFFFF',
      letterSpacing: '0.05em',
    },
    h6: {
      fontFamily: '"Orbitron", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '1rem',
      fontWeight: 500,
      color: '#FFFFFF',
      letterSpacing: '0.05em',
    },
    subtitle1: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '1rem',
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
    body1: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '1rem',
      letterSpacing: '0.02em',
    },
    button: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          background: 'rgba(10,24,51,0.85)',
          borderRadius: 16,
          boxShadow: '0 8px 32px 0 rgba(0,0,0,0.25)',
          padding: '2rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: '#1a2238',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          letterSpacing: '0.04em',
        },
      },
    },
  },
});

// Function to calculate total lap time from lapId
const getTotalLapTime = (lapId) => {
    const lapTimeMatch = lapId.match(/lap_(\d+)-(\d+)-(\d+)\.json/);
    if (lapTimeMatch) {
        const minutes = parseInt(lapTimeMatch[1]);
        const seconds = parseInt(lapTimeMatch[2]);
        const milliseconds = parseInt(lapTimeMatch[3]);
        return (minutes * 60) + seconds + (milliseconds / 1000); // Total time in seconds
    }
    return 0; // Return 0 if the format is not matched
};

const SESSION_COOKIE = 'session_uuid';
const JWT_COOKIE = 'session_token';
const SESSION_DURATION_MS = 60 * 60 * 1000; // 1 hour

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  // --- All hooks at the top ---
  const [laps, setLaps] = useState([]);
  const [selectedLaps, setSelectedLaps] = useState([]);
  const [lapData, setLapData] = useState({});
  const [trackRange, setTrackRange] = useState({ start: 0, end: 100 });
  const [totalLapTime, setTotalLapTime] = useState(0);
  const [inputsMode, setInputsMode] = useState('individual'); // 'individual' or 'merged'
  const [mergedInputType, setMergedInputType] = useState('throttle'); // 'throttle', 'brake', 'speed'
  const [trackLayouts, setTrackLayouts] = useState({});
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [sessionUuid, setSessionUuid] = useState(Cookies.get(SESSION_COOKIE));
  const [jwtToken, setJwtToken] = useState(Cookies.get(JWT_COOKIE));
  const [queueId, setQueueId] = useState(null);
  const [showQueuePage, setShowQueuePage] = useState(false);
  const [showRenewWarning, setShowRenewWarning] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [showUploadError, setShowUploadError] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [autoDownload, setAutoDownload] = useState(false);
  const [backendError, setBackendError] = useState(false);
  const [backendSessionValid, setBackendSessionValid] = useState(true);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [showFinalizing, setShowFinalizing] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [trackDetectStatus, setTrackDetectStatus] = useState(null);
  const [normalizedTracks, setNormalizedTracks] = useState(null);
  const [corners, setCorners] = useState([]);
  // Add global x-axis mode state
  const [inputsXAxisMode, setInputsXAxisMode] = useState('distance'); // 'time' or 'distance'
  // Prevent double session creation (React 18 StrictMode)
  const sessionRequestSent = useRef(false);
  const autoDownloadTimerRef = useRef(null);
  const [showDownloads, setShowDownloads] = useState(false);
  const [sessionInfoOpen, setSessionInfoOpen] = useState(true);

  // Block dashboard rendering if restoring queue state from localStorage
  const restoringQueue = (
    localStorage.getItem('queue_id') &&
    localStorage.getItem('pending_session_uuid') &&
    (!sessionUuid || !jwtToken)
  );

  // --- SESSION/QUEUE LOGIC ---
  useEffect(() => {
    // Remove sessionExpiry and related logic
    const storedQueueId = localStorage.getItem('queue_id');
    const storedSessionUuid = localStorage.getItem('pending_session_uuid');

    // Always restore queue state from localStorage if present
    if (storedQueueId && storedSessionUuid) {
      setQueueId(storedQueueId);
      setShowQueuePage(true);
      return; // Do NOT POST to /session again!
    }

    // Otherwise, only POST if not already in queue and no valid session
    if (
      !sessionRequestSent.current &&
      (
        !sessionUuid ||
        !jwtToken
      )
    ) {
      sessionRequestSent.current = true;
      const newSessionUuid = uuidv4();
      fetch(`${BACKEND_URL}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_uuid: newSessionUuid })
      })
        .then(async res => {
          if (res.status === 429) {
            const data = await res.json();
            if (data.queue_id) {
              setSessionUuid(null);
              setJwtToken(null);
              setQueueId(data.queue_id);
              setShowQueuePage(true);
              localStorage.setItem('queue_id', data.queue_id);
              localStorage.setItem('pending_session_uuid', newSessionUuid);
            }
            return null;
          }
          if (!res.ok) {
            return null;
          }
          return res.json();
        })
        .then(data => {
          if (data && data.token) {
            setSessionUuid(newSessionUuid);
            setJwtToken(data.token);
            const jwtInfo = parseJwt(data.token);
            let expiresDate = 1/24; // fallback: 1 hour
            if (jwtInfo && jwtInfo.exp) {
              const nowSec = Math.floor(Date.now() / 1000);
              expiresDate = (jwtInfo.exp - nowSec) / (60 * 60 * 24); // days
            }
            Cookies.set(SESSION_COOKIE, newSessionUuid, { expires: expiresDate, secure: isHttps, sameSite: 'Strict', httpOnly: false });
            Cookies.set(JWT_COOKIE, data.token, { expires: expiresDate, secure: isHttps, sameSite: 'Strict', httpOnly: false });
            localStorage.removeItem('queue_id');
            localStorage.removeItem('pending_session_uuid');
            setTimeout(() => window.location.reload(), 100);
          }
        });
    }
  }, [sessionUuid, jwtToken]);

  // Only poll and try to start session if on the queue page
  useEffect(() => {
    if (!queueId || !showQueuePage) return;
    let interval;
    let active = true;
    async function pollQueueAndStart() {
      try {
        const res = await fetch(`${BACKEND_URL}/queue/${queueId}`);
        const data = await res.json();
        if (data.position === 1 && active) {
          // At front, try to start session
          const pendingSessionUuid = localStorage.getItem('pending_session_uuid');
          const sessionRes = await fetch(`${BACKEND_URL}/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ queue_id: queueId, session_uuid: pendingSessionUuid, start_session: true })
          });
          if (sessionRes.status === 429) {
            // Redirect to queue page if rate limited
            setShowQueuePage(true);
            return;
          }
          if (sessionRes.ok) {
            const sessionData = await sessionRes.json();
            setSessionUuid(pendingSessionUuid);
            setJwtToken(sessionData.token);
            Cookies.set(SESSION_COOKIE, pendingSessionUuid, { expires: 1/24, secure: isHttps, sameSite: 'Strict', httpOnly: false });
            Cookies.set(JWT_COOKIE, sessionData.token, { expires: 1/24, secure: isHttps, sameSite: 'Strict', httpOnly: false });
            localStorage.removeItem('queue_id');
            localStorage.removeItem('pending_session_uuid');
            window.location.reload();
          }
        }
      } catch {}
    }
    pollQueueAndStart();
    interval = setInterval(pollQueueAndStart, 60000); // poll every 1 minute
    return () => { active = false; clearInterval(interval); };
  }, [queueId, showQueuePage]);

  // On mount, restore queueId/sessionUuid from localStorage if present
  useEffect(() => {
    const storedQueueId = localStorage.getItem('queue_id');
    const storedSessionUuid = localStorage.getItem('pending_session_uuid');
    if (storedQueueId && storedSessionUuid && (!sessionUuid || !jwtToken)) {
      setQueueId(storedQueueId);
      setShowQueuePage(true);
    }
  }, [sessionUuid, jwtToken]);

  // Only fetch data if we have a valid session
  const hasValidSession = !!(sessionUuid && jwtToken && !showQueuePage && !queueId);

  // Fetch lap list from backend
  useEffect(() => {
    if (!hasValidSession) return;
    authFetch(`${BACKEND_URL}/laps/`)
      .then(res => {
        if (res && res.ok) return res.json();
        return null;
      })
      .then(data => {
        if (data) setLaps(data);
      })
      .catch(e => {
        if (e.message === 'BACKEND_NETWORK_ERROR') setBackendError(true);
      });
  }, [hasValidSession]);

  // Fetch track list and normalized tracks only on mount
  useEffect(() => {
    if (!hasValidSession) return;
    Promise.all([
      authFetch(`${BACKEND_URL}/tracks/`).then(res => res && res.ok ? res.json() : null),
      authFetch(`${BACKEND_URL}/tracks/normalized/`).then(res => res && res.ok ? res.json() : null)
    ]).then(([trackList, normalized]) => {
      if (trackList) {
        const formattedTracks = trackList
          .map(track => ({
            original: track,
            display: track.replace(/_/g, '  ').replace(/-/g, ' - ')
          }))
          .sort((a, b) => a.display.localeCompare(b.display));
        setTracks(formattedTracks);
      }
      if (normalized) setNormalizedTracks(normalized);
    }).catch(e => {
      if (e.message === 'BACKEND_NETWORK_ERROR') setBackendError(true);
    });
  }, [hasValidSession]);

  // Fetch selected track layout only when selectedTrack changes and not already loaded
  useEffect(() => {
    if (!hasValidSession) return;
    if (!selectedTrack || trackLayouts[selectedTrack]) return;
    authFetch(`${BACKEND_URL}/tracks/${selectedTrack}`)
      .then(res => {
        if (res && res.ok) return res.json();
        return null;
      })
      .then(layout => {
        if (layout) setTrackLayouts(prev => ({ ...prev, [selectedTrack]: layout }));
      })
      .catch(e => {
        if (e.message === 'BACKEND_NETWORK_ERROR') setBackendError(true);
      });
  }, [selectedTrack, trackLayouts, hasValidSession]);

  // Fetch corners for selected track
  useEffect(() => {
    if (!hasValidSession) return;
    if (!selectedTrack) {
      setCorners([]);
      return;
    }
    authFetch(`${BACKEND_URL}/tracks/${selectedTrack}/corners`)
      .then(res => res && res.ok ? res.json() : null)
      .then(data => {
        if (data && Array.isArray(data.corners)) setCorners(data.corners);
        else if (Array.isArray(data)) setCorners(data);
        else setCorners([]);
      })
      .catch(() => setCorners([]));
  }, [selectedTrack, hasValidSession]);

  // Fetch lap data when selected
  useEffect(() => {
    if (!hasValidSession) return;
    // Fetch lap data for selected laps
    selectedLaps.forEach(lapId => {
        if (!lapData[lapId]) {
            authFetch(`${BACKEND_URL}/laps/${lapId}`)
                .then(res => {
                  if (res && res.ok) return res.json();
                  return null;
                })
                .then(data => {
                    if (data) {
                      setLapData(prev => ({ ...prev, [lapId]: data }));
                    }
                })
                .catch(e => {
                  if (e.message === 'BACKEND_NETWORK_ERROR') setBackendError(true);
                });
        }
    });
    // Always reset to 0-100% for new selection
    setTrackRange({ start: 0, end: 100 });
  }, [selectedLaps, lapData, hasValidSession]);

  // --- Ensure graphs are updated when selectedLaps or timeRange changes ---
  // useEffect(() => {
  //   // This effect will trigger re-renders of the graphs based on the updated selectedLaps and timeRange
  // }, [selectedLaps, timeRange]);

  const handleTrackRangeChange = (event, newValue) => {
    setTrackRange({
      start: newValue[0],
      end: newValue[1]
    });
  };

  const handleAutoDetectTrack = (detectedTrack) => {
    if (detectedTrack && tracks.some(t => t.original === detectedTrack)) {
      setSelectedTrack(detectedTrack);
    }
  };

  const handleResetTrackRange = () => {
    setTrackRange({ start: 0, end: 100 });
  };

  const handleLapRemoval = (lapId) => {
    setSelectedLaps(prev => {
        const newSelectedLaps = prev.filter(id => id !== lapId);
        
        // Update time range to match the longest lap time
        if (newSelectedLaps.length > 0) {
            const longestLapTime = Math.max(...newSelectedLaps.map(id => getTotalLapTime(id)));
            setTrackRange({ start: 0, end: longestLapTime });
        } else {
            // Reset time range if no laps are selected
            setTrackRange({ start: 0, end: 0 });
        }
        return newSelectedLaps;
    });
  };

  // Reset selectedTrack to None when all laps are removed
  useEffect(() => {
    if (selectedLaps.length === 0) {
      setSelectedTrack("");
    }
  }, [selectedLaps]);

  // Format time in seconds to mm:ss.ms format
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    // No milliseconds
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Convert time range to data points range
  const getDataPointRange = () => {
    if (!selectedLaps.length) return null;
    // Find the lapId with the longest lap time
    let longestLapId = selectedLaps[0];
    let maxTime = getTotalLapTime(longestLapId);
    selectedLaps.forEach(lapId => {
      const t = getTotalLapTime(lapId);
      if (t > maxTime) {
        maxTime = t;
        longestLapId = lapId;
      }
    });
    const data = lapData[longestLapId];
    if (!data) return null;
    const timeStep = totalLapTime / (data.length - 1);
    return {
      start: Math.floor(trackRange.start / timeStep),
      end: Math.ceil(trackRange.end / timeStep)
    };
  };

  // Handler for deleting laps
  const handleDeleteLap = (lapId) => {
    if (lapId === 'ALL') {
      authFetch(`${BACKEND_URL}/laps`, { method: 'DELETE' })
        .then(res => {
          if (res.ok) setLaps([]);
        });
    } else {
      authFetch(`${BACKEND_URL}/laps/${lapId}`, { method: 'DELETE' })
        .then(res => {
          if (res.ok) setLaps(laps => laps.filter(l => l !== lapId));
        });
    }
    setSelectedLaps([]);
    setLapData({});
  };

  // Download laps as JSON files (accepts array of lap IDs)
  const onDownloadLaps = (lapIds) => handleDownloadLaps({ lapIds, authFetch, BACKEND_URL, sessionUuid, setDownloadStatus });

  // Upload laps from JSON files
  const onUploadLaps = (files) => handleUploadLaps({ files, setUploadStatus, setUploadError, setShowUploadError, setUploadSuccess, setShowUploadSuccess, authFetch, BACKEND_URL, setLaps });

  // Parse JWT expiry
  function parseJwt(token) {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  }

  // Periodic backend session validity check (every minute)
  useEffect(() => {
    if (!jwtToken) return;
    let interval = setInterval(() => {
      authFetch(`${BACKEND_URL}/session/valid`, { headers: { 'Authorization': `Bearer ${jwtToken}` } })
        .then(async res => {
          if (!res.ok) {
            setBackendSessionValid(false);
            return;
          }
          const data = await res.json();
          if (data.valid === false) {
            setBackendSessionValid(false);
          } else {
            setBackendSessionValid(true);
          }
        })
        .catch(() => {
          // If network error, don't mark session invalid, just ignore
        });
    }, 60000); // every 1 minute
    // Run once immediately
    authFetch(`${BACKEND_URL}/session/valid`, { headers: { 'Authorization': `Bearer ${jwtToken}` } })
      .then(async res => {
        if (!res.ok) {
          setBackendSessionValid(false);
          return;
        }
        const data = await res.json();
        if (data.valid === false) {
          setBackendSessionValid(false);
        } else {
          setBackendSessionValid(true);
        }
      })
      .catch(() => {});
    return () => clearInterval(interval);
  }, [jwtToken]);

  // Parse JWT expiry
  const jwtInfo = parseJwt(jwtToken);
  let expiryString = '';
  let isSessionExpired = false;
  if (jwtInfo && jwtInfo.exp) {
    const expiryDate = new Date(jwtInfo.exp * 1000);
    expiryString = expiryDate.toLocaleString();
    isSessionExpired = Date.now() > expiryDate.getTime();
  }
  // Combine frontend and backend session validity
  const sessionReallyExpired = isSessionExpired || !backendSessionValid;

  // Heartbeat: keep session alive while window/tab is open
  useEffect(() => {
    if (!jwtToken || sessionReallyExpired) return;
    let interval = setInterval(() => {
      authFetch(`${BACKEND_URL}/session/heartbeat`, { method: 'POST', headers: { 'Authorization': `Bearer ${jwtToken}` } });
    }, 60000); // every 1 minute
    // Send one immediately
    authFetch(`${BACKEND_URL}/session/heartbeat`, { method: 'POST', headers: { 'Authorization': `Bearer ${jwtToken}` } });
    return () => clearInterval(interval);
  }, [jwtToken, sessionReallyExpired]);

  // Auto-download logic: set timer when enabled or when JWT expiry changes
  useEffect(() => {
    if (!autoDownload) {
      if (autoDownloadTimerRef.current) {
        clearTimeout(autoDownloadTimerRef.current);
        autoDownloadTimerRef.current = null;
      }
      return;
    }
    if (!jwtInfo || !jwtInfo.exp) return;
    const expiryMs = jwtInfo.exp * 1000;
    const now = Date.now();
    const msUntilDownload = expiryMs - now - 60 * 1000; // 1 min before expiry
    if (msUntilDownload > 0) {
      autoDownloadTimerRef.current = setTimeout(async () => {
        // Always fetch the latest lap list before downloading
        try {
          const res = await authFetch(`${BACKEND_URL}/laps/`);
          let latestLaps = laps;
          if (res && res.ok) {
            const data = await res.json();
            if (Array.isArray(data)) latestLaps = data;
          }
          await onDownloadLaps(latestLaps);
        } catch (e) {
          // fallback: try with current laps state
          await onDownloadLaps(laps);
        }
      }, msUntilDownload);
    }
    // If less than 1 min left, do nothing (no download, no timer)
    return () => {
      if (autoDownloadTimerRef.current) {
        clearTimeout(autoDownloadTimerRef.current);
        autoDownloadTimerRef.current = null;
      }
    };
  }, [autoDownload, jwtInfo, laps]);

  // --- Renew logic: always delete session and reload
  const doSessionRenew = async () => {
    if (jwtToken) {
      await fetch(BACKEND_URL + '/session', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      });
    }
    Cookies.remove(SESSION_COOKIE);
    Cookies.remove(JWT_COOKIE);
    window.location.reload();
  };

  // Handler for Renew button (shows warning if session is active)
  const handleRenewClick = () => {
    if (!sessionReallyExpired) {
      setShowRenewWarning(true);
      return;
    }
    doSessionRenew();
  };

  // --- MAIN RENDER LOGIC ---
  let mainContent = null;
  if (restoringQueue || (showQueuePage && (queueId || localStorage.getItem('queue_id')))) {
    mainContent = <QueuePage 
      queueId={queueId || localStorage.getItem('queue_id')} 
      onReady={(sessionUuid, token) => {
        setSessionUuid(sessionUuid);
        setJwtToken(token);
        const jwtInfo = parseJwt(token);
        let expiresDate = 1/24; // fallback: 1 hour
        if (jwtInfo && jwtInfo.exp) {
          const nowSec = Math.floor(Date.now() / 1000);
          expiresDate = (jwtInfo.exp - nowSec) / (60 * 60 * 24); // days
        }
        Cookies.set(SESSION_COOKIE, sessionUuid, { expires: expiresDate, secure: isHttps, sameSite: 'Strict', httpOnly: false });
        Cookies.set(JWT_COOKIE, token, { expires: expiresDate, secure: isHttps, sameSite: 'Strict', httpOnly: false });
        setShowQueuePage(false);
        setQueueId(null);
      }} 
      backendUrl={BACKEND_URL} 
    />;
  } else if (showDownloads) {
    mainContent = (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Button variant="outlined" onClick={() => setShowDownloads(false)} sx={{ mb: 2 }}>
            Back to Dashboard
          </Button>
          <DownloadsPage />
        </Container>
      </ThemeProvider>
    );
  } else {
    mainContent = (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* JWT Token Display Top Right (short version) */}
        <SessionInfoBox
          sessionReallyExpired={sessionReallyExpired}
          autoDownload={autoDownload}
          setAutoDownload={setAutoDownload}
          sessionInfoOpen={sessionInfoOpen}
          setSessionInfoOpen={setSessionInfoOpen}
          jwtToken={jwtToken}
          expiryString={expiryString}
          sessionUuid={sessionUuid}
          setShowDownloads={setShowDownloads}
          setShowRenewWarning={setShowRenewWarning}
          showRenewWarning={showRenewWarning}
          BACKEND_URL={BACKEND_URL}
          Cookies={Cookies}
          SESSION_COOKIE={SESSION_COOKIE}
          JWT_COOKIE={JWT_COOKIE}
          windowReload={doSessionRenew}
          handleRenewClick={handleRenewClick}
        />
        {/* Show logo below session box on mobile only */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mt: 2 }}>
          <img
            src="gtt-logo.png"
            alt="GT Telemetry logo"
            style={{
              height: 'auto',
              width: '100%',
              maxWidth: '240px',
              marginBottom: 8,
              display: 'block',
            }}
            sizes="(max-width: 600px) 80px, (max-width: 900px) 120px, 240px"
          />
        </Box>
        <GlobalStyles styles={{
          body: {
            background: 'linear-gradient(135deg, #0a1833 0%, #1a2238 100%)',
            minHeight: '100vh',
          }
        }} />
        {/* AdSense Side Banners - desktop only */}
        <AdBannerSide position="left" />
        <AdBannerSide position="right" />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* AdSense Banner - main page */}
          <AdBanner style={{ marginBottom: 24 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'center' },
              gap: { xs: 2, md: 4 },
              mb: 3,
            }}
          >
            {/* Hide logo in header on mobile, show on md+ */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', mb: { xs: 2, md: 0 } }}>
              <img
                src="gtt-logo.png"
                alt="GT Telemetry logo"
                style={{
                  height: 'auto',
                  width: '100%',
                  maxWidth: '240px',
                  marginBottom: 8,
                  display: 'block',
                }}
                sizes="(max-width: 600px) 80px, (max-width: 900px) 120px, 240px"
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' } }}>
            <HowToButton />
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
                  fontWeight: 900,
                  fontStyle: 'normal',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  color: '#fff',
                  lineHeight: 1.1,
                  mb: 0,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                  textAlign: { xs: 'center', md: 'left' },
                  wordBreak: 'break-word',
                }}
              >
                Racing telemetry analyzer<br /><br />
              </Typography>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
                  fontWeight: 900,
                  fontStyle: 'normal',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  color: '#fff',
                  lineHeight: 1.1,
                  mb: 0,
                  fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2rem' },
                  textAlign: { xs: 'center', md: 'left' },
                  wordBreak: 'break-word',
                }}
              >
                Compatible with Gran Turismo Games
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <LapControls
              laps={laps}
              onDownload={onDownloadLaps}
              onUpload={onUploadLaps}
            />
            <LapList 
              laps={laps} 
              selectedLaps={selectedLaps} 
              setSelectedLaps={setSelectedLaps} 
            />
            <DeleteLapsDropdown laps={laps} onDelete={handleDeleteLap} />
            <FormControl sx={{ minWidth: 220, mt: 2 }} size="small">
              <InputLabel id="track-select-label">Select Track Layout</InputLabel>
              <Select
                labelId="track-select-label"
                value={selectedTrack}
                label="Select Track Layout"
                onChange={e => setSelectedTrack(e.target.value)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {tracks.map(track => (
                  <MenuItem key={track.original} value={track.original}>{track.display}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Track detection status message below dropdown */}
            {trackDetectStatus && (
              <Box sx={{
                color: '#fff',
                bgcolor: 'rgba(255,23,68,0.08)', // transparent red background
                border: '2px solid #ff1744', // red border
                p: 1.5,
                borderRadius: 1,
                fontSize: '0.95rem',
                mt: 1,
                fontWeight: 500,
                boxShadow: 'none',
              }}>
                <b>Track Detection:</b> {trackDetectStatus}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {(selectedLaps.length > 0 || selectedTrack) && (
              <RaceLineReplay
                timeRange={trackRange}
                setTimeRange={setTrackRange}
                handleResetTimeRange={() => setTrackRange({ start: 0, end: 100 })}
                selectedLaps={selectedLaps}
                lapData={lapData}
                trackLayout={selectedTrack ? trackLayouts[selectedTrack] : null}
                onAutoDetectTrack={handleAutoDetectTrack}
                onTrackDetectStatus={setTrackDetectStatus}
                normalizedTracks={normalizedTracks}
                selectedTrack={selectedTrack}
                corners={corners}
              />
            )}
            {/* Lap Comparison - NEW COMPONENT (moved here) */}
            {selectedLaps.length === 2 && corners.length > 0 && lapData[selectedLaps[0]] && lapData[selectedLaps[1]] && (
              <LapComparison
                fastestLap={getTotalLapTime(selectedLaps[0]) < getTotalLapTime(selectedLaps[1]) ? lapData[selectedLaps[0]] : lapData[selectedLaps[1]]}
                otherLap={getTotalLapTime(selectedLaps[0]) < getTotalLapTime(selectedLaps[1]) ? lapData[selectedLaps[1]] : lapData[selectedLaps[0]]}
                corners={corners}
                backendUrl={BACKEND_URL}
              />
            )}
            {/* Inputs Chart Mode Toggle */}
            {selectedLaps.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ToggleButtonGroup
                  value={inputsMode}
                  exclusive
                  onChange={(_, value) => value && setInputsMode(value)}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="individual">Individual Inputs Charts</ToggleButton>
                  {selectedLaps.length > 1 && (
                    <ToggleButton value="merged">Merged Inputs Chart</ToggleButton>
                  )}
                </ToggleButtonGroup>
                {/* X-Axis Mode Toggle */}
                <ToggleButtonGroup
                  value={inputsXAxisMode}
                  exclusive
                  onChange={(_, value) => value && setInputsXAxisMode(value)}
                  size="small"
                  color="primary"
                  sx={{ ml: 2 }}
                >
                  <ToggleButton value="time">Time</ToggleButton>
                  <ToggleButton value="distance">Distance</ToggleButton>
                </ToggleButtonGroup>
                {inputsMode === 'merged' && selectedLaps.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="merged-input-type-label">Input</InputLabel>
                    <Select
                      labelId="merged-input-type-label"
                      value={mergedInputType}
                      label="Input"
                      onChange={e => setMergedInputType(e.target.value)}
                    >
                      <MenuItem value="throttle">Throttle</MenuItem>
                      <MenuItem value="brake">Brake</MenuItem>
                      <MenuItem value="speed">Speed</MenuItem>
                      <MenuItem value="gear">Gear</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
            {/* Inputs Graphs */}
            {inputsMode === 'individual' && selectedLaps.map(lapId => (
              lapData[lapId] && (
                <InputsGraph 
                  key={lapId}
                  lap={lapData[lapId]} 
                  timeRange={trackRange}
                  lapId={lapId}
                  inputsXAxisMode={inputsXAxisMode}
                />
              )
            ))}
            {inputsMode === 'merged' && selectedLaps.length > 0 && (
              <InputsGraph
                lap={selectedLaps.map(lapId => lapData[lapId])}
                timeRange={trackRange}
                lapId={selectedLaps}
                merged
                mergedInputType={mergedInputType}
                selectedLaps={selectedLaps}
                inputsXAxisMode={inputsXAxisMode}
              />
            )}
          </Box>
        </Container>
        <Snackbar open={showUploadError} autoHideDuration={6000} onClose={() => setShowUploadError(false)}>
          <MuiAlert elevation={6} variant="filled" onClose={() => setShowUploadError(false)} severity="error">
            {uploadError}
          </MuiAlert>
        </Snackbar>
        <Snackbar open={showUploadSuccess} autoHideDuration={4000} onClose={() => setShowUploadSuccess(false)}>
          <MuiAlert elevation={6} variant="filled" onClose={() => setShowUploadSuccess(false)} severity="success">
            {uploadSuccess}
          </MuiAlert>
        </Snackbar>
        <Snackbar open={backendError} autoHideDuration={6000} onClose={() => setBackendError(false)}>
          <MuiAlert elevation={6} variant="filled" onClose={() => setBackendError(false)} severity="error" sx={{ backgroundColor: '#FF1E1E', color: '#fff', fontWeight: 700 }}>
            Connection to Backend Lost
          </MuiAlert>
        </Snackbar>
        {/* In the return JSX, show upload status if present or finalizing */}
        {(uploadStatus || showFinalizing) && (
          <Box sx={{ position: 'fixed', top: 80, left: 0, width: '100%', zIndex: 3000, textAlign: 'center' }}>
            <Box sx={{ display: 'inline-block', bgcolor: '#222', color: '#fff', px: 3, py: 1, borderRadius: 2, boxShadow: 3, fontWeight: 600, fontSize: '1.1rem' }}>
              {showFinalizing
                ? 'Finalizing...'
                : uploadStatus && uploadStatus.status === 'uploading' && uploadStatus.progress < 98
                ? <>
                    Uploading laps...{uploadStatus.speed && (
                      <> | Speed: {(uploadStatus.speed / 1024 / 1024).toFixed(2)} MB/s</>
                    )}
                    {uploadStatus.eta !== null && uploadStatus.eta > 0 && (
                      <> | ETA: {Math.ceil(uploadStatus.eta)}s</>
                    )}
                  </>
                : uploadStatus && uploadStatus.status === 'done'
                ? 'Upload complete!'
                : (!showFinalizing && (!uploadStatus.eta || uploadStatus.eta === 0))
                ? 'Finalizing upload...'
                : null
              }
            </Box>
          </Box>
        )}
        {/* In the return JSX, show download status if present */}
        {(downloadStatus) && (
          <Box sx={{ position: 'fixed', top: 130, left: 0, width: '100%', zIndex: 3000, textAlign: 'center' }}>
            <Box sx={{ display: 'inline-block', bgcolor: '#222', color: '#fff', px: 3, py: 1, borderRadius: 2, boxShadow: 3, fontWeight: 600, fontSize: '1.1rem' }}>
              {downloadStatus.status === 'downloading' && downloadStatus.progress < 98 ? (
                <>
                  Downloading laps...{downloadStatus.speed && (
                    <> | Speed: {(downloadStatus.speed / 1024 / 1024).toFixed(2)} MB/s</>
                  )}
                  {downloadStatus.eta !== null && downloadStatus.eta > 0 && (
                    <> | ETA: {Math.ceil(downloadStatus.eta)}s</>
                  )}
                </>
              ) : downloadStatus.status === 'done' ? 'Download complete!' : null}
            </Box>
          </Box>
        )}
        <Footer />
      </ThemeProvider>
    );
  }

  const getPageFromPath = () => {
    if (window.location.pathname === "/privacy") return "privacy";
    if (window.location.pathname === "/terms") return "terms";
    return null;
  };

  return (
    <SimpleRouter page={getPageFromPath()}>
      {mainContent}
      <CookieConsent />
    </SimpleRouter>
  );
}

const isHttps = window.location.protocol === "https:";

export default App;
